import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Crown, Gavel, Loader2, User as UserIcon, Wand2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { formatGBP } from "@/lib/format";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 10;

type HistoryRow = {
  id: string;
  amount_pence: number;
  created_at: string;
  bidder_label: string;
  is_me: boolean;
  is_auto_bid: boolean;
};

async function fetchPage(auctionId: string, offset: number): Promise<HistoryRow[]> {
  const { data, error } = await supabase.rpc("get_auction_bid_history", {
    _auction_id: auctionId,
    _limit: PAGE_SIZE,
    _offset: offset,
  });
  if (error) throw error;
  return (data ?? []) as HistoryRow[];
}

/**
 * Public bid history. Names are masked server-side by
 * `get_auction_bid_history` (Jer***B), except the viewer's own bids.
 * Pages lazily so long auctions never ship hundreds of rows up front.
 */
export function BidHistory({
  auctionId,
  bidCount,
  isEnded,
}: {
  auctionId: string;
  bidCount: number;
  isEnded: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const query = useInfiniteQuery({
    queryKey: ["auction-bid-history", auctionId],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => fetchPage(auctionId, pageParam as number),
    getNextPageParam: (last, all) =>
      last.length < PAGE_SIZE ? undefined : all.length * PAGE_SIZE,
    staleTime: 10_000,
  });

  const rows = (query.data?.pages ?? []).flat();
  const visible = expanded ? rows : rows.slice(0, PAGE_SIZE);
  const myTop = rows.findIndex((r) => r.is_me);
  const leadingIsMe = rows.length > 0 && rows[0].is_me;

  return (
    <section className="mt-8" aria-labelledby="bid-history-heading">
      <div className="mb-3 flex items-end justify-between gap-3">
        <h2 id="bid-history-heading" className="font-display text-2xl">
          Bid history
        </h2>
        <span className="text-xs text-muted-foreground">
          {bidCount} bid{bidCount === 1 ? "" : "s"}
        </span>
      </div>

      {myTop >= 0 && (
        <p
          role="status"
          aria-live="polite"
          className={cn(
            "mb-3 rounded-lg border px-3 py-2 text-sm",
            leadingIsMe
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
              : "border-amber-500/40 bg-amber-500/10 text-amber-300",
          )}
        >
          {leadingIsMe
            ? isEnded
              ? "You placed the winning bid."
              : "You are currently the highest bidder."
            : isEnded
              ? "You were outbid on this auction."
              : "You have been outbid — place a higher bid to take the lead."}
        </p>
      )}

      <div className="min-h-[120px] rounded-xl border border-border/60 bg-card divide-y divide-border/60">
        {query.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-9 rounded-md bg-muted/30 animate-pulse" />
            ))}
          </div>
        ) : query.isError ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Bid history is temporarily unavailable.
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            {bidCount > 0
              ? "No bid history to display for this auction."
              : "No bids yet — be the first!"}
          </div>
        ) : (
          visible.map((b, idx) => (
            <div
              key={b.id}
              className={cn(
                "flex items-center justify-between gap-3 px-4 py-3 text-sm transition-colors",
                idx === 0 && "bg-emerald-500/[0.05]",
                b.is_me && idx !== 0 && "bg-primary/[0.04]",
              )}
            >
              <div className="flex min-w-0 items-center gap-2">
                {idx === 0 ? (
                  <Crown className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden />
                ) : (
                  <UserIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                )}
                <span className={cn("truncate", b.is_me ? "font-medium text-gold" : "text-foreground/90")}>
                  {b.bidder_label}
                </span>
                {idx === 0 && (
                  <span className="shrink-0 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-emerald-400">
                    {isEnded ? "Winning bid" : "Highest bid"}
                  </span>
                )}
                {b.is_auto_bid && (
                  <span
                    className="hidden shrink-0 items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground sm:inline-flex"
                    title="Placed automatically by a maximum bid"
                  >
                    <Wand2 className="h-2.5 w-2.5" aria-hidden /> Auto
                  </span>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <time
                  dateTime={b.created_at}
                  className="hidden text-xs text-muted-foreground sm:block"
                >
                  {new Date(b.created_at).toLocaleString("en-GB")}
                </time>
                <span className="text-xs text-muted-foreground sm:hidden">
                  {new Date(b.created_at).toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <span className="font-medium text-gold tabular-nums">{formatGBP(b.amount_pence)}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {rows.length > PAGE_SIZE && !expanded ? (
        <Button
          variant="outline"
          size="sm"
          className="mt-3 w-full border-border/60"
          onClick={() => setExpanded(true)}
        >
          Show all loaded bids
        </Button>
      ) : query.hasNextPage ? (
        <Button
          variant="outline"
          size="sm"
          className="mt-3 w-full border-border/60"
          disabled={query.isFetchingNextPage}
          onClick={() => {
            setExpanded(true);
            query.fetchNextPage();
          }}
        >
          {query.isFetchingNextPage ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Gavel className="mr-2 h-4 w-4" />
          )}
          Load more bids
        </Button>
      ) : null}
    </section>
  );
}
